import React, { useMemo, useState } from 'react';
import { OneWayInOpportunityStatusDto, OneWayInStatus } from 'src/api/v2';
import Table from 'src/components/Table';
import HistoryTableSection, { HistoryTableSectionProps } from './HistoryTableSection';
import { Dialog, Skeleton } from '@innovationnorway/cl-react';
import { useTranslation } from 'react-i18next';
import useOneWayIn from '../../../hooks/use-one-way-in';
import HistoryViewEmailContent from './HistoryViewEmailContent';
import { formatDateToNOString } from 'src/utils/FormatValue';
import RecommendServiceMailCustomer from './RecommendServiceMailCustomer';
import { CustomerMailRecommendedServices } from '../../../models/OneWayInModels';
import HistoryTableINSection from './HistoryTableINSection';
import { sanitizeMinSideEmail } from '../../../utils/htmlTextConverter';
import { MINSIDE_REFERRALPARTY_FIRST_LINE_FILTER } from 'src/features/one-way-in/constants/firstLineReferralParty';

import './OneWayInHistory.scss';
import useOneWayInVMA from 'src/features/one-way-in/hooks/use-one-way-in-vma';
import useSiteimprove from 'src/hooks/use-siteimprove';
import { SITEIMPROVE_DECLARATIONS } from 'src/features/tracking/constants';
import ReferralMailActor from './ReferralMailActor';
import EmailPreview from './EmailPreview';

type Props = {
  data: OneWayInOpportunityStatusDto[];
  isLoading?: boolean;
  formId: string;
};

export interface EmailViewContent {
  title: string;
  from: string;
  to: string;
  date: string;
  text: string;
  recommendedMail: boolean;
  referralMailActor: boolean;
  referralMailCustomer: boolean;
  actorName?: string;
  recommendedServices?: CustomerMailRecommendedServices[];
  handlerName?: string;
  handlerEmail?: string;
  handlerPhone?: string;
  opportunityTitle?: string;
  receivingActor?: string;
  fromActorName?: string;
}

const OneWayInHistoryV2: React.FC<Props> = ({ data, isLoading, formId }) => {
  const { t: tOneWayIn } = useTranslation('oneWayIn');
  const siteimprove = useSiteimprove();

  const {
    oneWayInGroupedFormWOEnrollmentsQuery,
    oneWayInOpportunityById,
    onewWayInOpportunityFormINResponseMailQuery,
  } = useOneWayIn(formId);
  const { oneWayInAllVMAsQuery } = useOneWayInVMA();

  const [emailViewActive, setEmailViewActive] = useState(false);

  const referredActors = useMemo(() => {
    const referredActors = oneWayInOpportunityById.data?.originalRecipients || [];
    const originalVMAs = oneWayInAllVMAsQuery.data || [];

    const filteredOriginalVMAs = originalVMAs.filter((vma) => {
      if (vma.id === MINSIDE_REFERRALPARTY_FIRST_LINE_FILTER) return true;
      return vma.isBusinessSupportOrganization;
    });

    return filteredOriginalVMAs.filter((vma) => referredActors.some((actor) => actor === vma.id));
  }, [oneWayInOpportunityById.data, oneWayInAllVMAsQuery.data]);

  const [emailContent, setEmailContent] = React.useState<EmailViewContent>({
    title: '',
    from: '',
    to: '',
    date: '',
    text: '',
    recommendedMail: false,
    referralMailActor: false,
    referralMailCustomer: false,
  });

  const openEmailView = (content: EmailViewContent) => {
    if (!content) return;

    setEmailContent(content);
    setEmailViewActive(true);
  };

  const openINEmailView = () => {
    siteimprove.log(SITEIMPROVE_DECLARATIONS.HISTORY.id, SITEIMPROVE_DECLARATIONS.HISTORY.actions.READ_REPLY);

    if (!onewWayInOpportunityFormINResponseMailQuery.data) return;

    setEmailContent({
      title: onewWayInOpportunityFormINResponseMailQuery.data.subject || '',
      from: '',
      to: '',
      date: formatDateToNOString(
        onewWayInOpportunityFormINResponseMailQuery.data.createdOn || new Date(),
        false,
        '{date}.{month}.{year}',
        true,
      ),
      text: onewWayInOpportunityFormINResponseMailQuery.data.body || '',
      recommendedMail: false,
      referralMailActor: false,
      referralMailCustomer: false,
    });

    setEmailViewActive(true);
  };

  const history = useMemo(() => {
    const record: Record<string, OneWayInOpportunityStatusDto[]> = {};

    data.forEach((item) => {
      // Ignore the received status, and new status.
      if (item.status === OneWayInStatus.Received || item.status === OneWayInStatus.New) return;

      let _date = new Date(item.createdOn.toString());
      if (item.deliveredOn) _date = new Date(item.deliveredOn.toString());
      const date = _date.getDate();
      const month = _date.getMonth();
      const year = _date.getFullYear();

      const key = new Date(year, month, date).toISOString();

      if (!record[key]) {
        record[key] = [];
      }

      record[key].push(item);
    });

    const keys = Object.keys(record)
      // dette sorterer fra eldst til nyest
      .sort()
      // så vi reverserer for å få nyeste først
      .reverse();

    const result = keys.map((key) => {
      return {
        date: new Date(key),
        data: [...record[key]].sort((a, b) => {
          const A = new Date(a.deliveredOn != undefined ? a.deliveredOn.toString() : a.createdOn.toString());
          const B = new Date(b.deliveredOn != undefined ? b.deliveredOn.toString() : b.createdOn.toString());

          // A - B = eldst først
          // så vi reverserer for å få nyeste først
          return B.getTime() - A.getTime();
        }),
      } as HistoryTableSectionProps;
    });

    return result;
  }, [data]);

  const getEmailContent = () => {
    if (emailContent.recommendedMail) return <RecommendServiceMailCustomer note={emailContent.text} />;

    if (emailContent.referralMailActor)
      return (
        <ReferralMailActor
          note={emailContent.text}
          opportunityTitle={emailContent.opportunityTitle || ''}
          receivingActor={emailContent.receivingActor || ''}
          fromActorName={emailContent.fromActorName || ''}
          handlerName={emailContent.handlerName || ''}
        />
      );
    return (
      <EmailPreview>
        <div dangerouslySetInnerHTML={{ __html: sanitizeMinSideEmail(emailContent.text) }} />
      </EmailPreview>
    );
  };

  return (
    <div className="one-way-in-history__page-div">
      <Table>
        <Table.Head>
          <Table.Row>
            <Table.Header style={{ width: '4.5rem' }}></Table.Header>
            <Table.Header>{tOneWayIn('log.table.column.activity')}</Table.Header>
            <Table.Header>{tOneWayIn('log.table.column.actor')}</Table.Header>
            <Table.Header>{tOneWayIn('log.table.column.behandler')}</Table.Header>
            <Table.Header>{tOneWayIn('log.table.column.description')}</Table.Header>
          </Table.Row>
        </Table.Head>

        <Table.Body>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, rowIndex) => (
              <Table.Row key={rowIndex}>
                {Array.from({ length: 5 }).map((_, cellIndex) => (
                  <Table.Cell key={cellIndex}>
                    <Skeleton />
                  </Table.Cell>
                ))}
              </Table.Row>
            ))
          ) : (
            <>
              {history.map(({ data, date }) => (
                <HistoryTableSection
                  key={date.toISOString()}
                  data={data}
                  date={date}
                  openEmailView={openEmailView}
                />
              ))}
              <HistoryTableINSection
                deliverDate={
                  onewWayInOpportunityFormINResponseMailQuery.data?.createdOn
                    ? formatDateToNOString(onewWayInOpportunityFormINResponseMailQuery.data?.createdOn)
                    : ''
                }
                receivedDate={oneWayInGroupedFormWOEnrollmentsQuery.data?.innsendtDato || ''}
                openEmailView={openINEmailView}
                hasEmail={!!onewWayInOpportunityFormINResponseMailQuery.data}
                referredActors={referredActors}
                isAutomaticReply={onewWayInOpportunityFormINResponseMailQuery.data?.isAutomaticReply}
              />
            </>
          )}
        </Table.Body>
      </Table>
      <Dialog
        open={emailViewActive}
        onClose={() => setEmailViewActive(false)}
        aria-label={emailContent.title}
        className="bold-title"
      >
        <HistoryViewEmailContent
          title={emailContent.title}
          from={emailContent.from}
          to={emailContent.to}
          showFromAndTo={emailContent.from !== '' || emailContent.to !== ''}
          date={emailContent.date}
          content={getEmailContent()}
          onClose={() => setEmailViewActive(false)}
        />
      </Dialog>
    </div>
  );
};

export default OneWayInHistoryV2;
